import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import MemberDetail from "@/pages/MemberDetail";
import Trainers from "@/pages/Trainers";
import TrainerDetail from "@/pages/TrainerDetail";
import MembershipPlans from "@/pages/MembershipPlans";
import Subscriptions from "@/pages/Subscriptions";
import Attendance from "@/pages/Attendance";
import Payments from "@/pages/Payments";
import FinancialLedger from "@/pages/FinancialLedger";
import Settings from "@/pages/Settings";
import CompanySettings from "@/pages/CompanySettings";
import { SettingsProvider } from "@/context/SettingsContext";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

// Authentication pages
import Login from "@/pages/auth/Login";
import SuperuserLogin from "@/pages/auth/SuperuserLogin";

// Layouts
import Layout from "@/components/Layout";
import SuperuserLayout from "@/components/SuperuserLayout";

// Protected routes
import { ProtectedRoute, PublicRoute } from "@/components/ProtectedRoute";

// Admin pages
import UserManagement from "@/pages/admin/UserManagement";

// Superuser pages
import CompanyManagement from "@/pages/superuser/CompanyManagement";

// Member pages
import MemberDashboard from "@/pages/member/MemberDashboard";
import MemberSubscriptions from "@/pages/member/MemberSubscriptions";
import MemberAttendance from "@/pages/member/MemberAttendance";
import MemberLedger from "@/pages/member/MemberLedger";
import MemberHealth from "@/pages/member/MemberHealth";

// Utils
import { isSuperuser, isMember, hasPermission } from "@/utils/auth";

function App() {
  const HomeRedirect = () => {
    if (isSuperuser()) {
      return <Navigate to="/superuser/dashboard" replace />;
    } else if (isMember()) {
      return <Navigate to="/member/dashboard" replace />;
    }
    
    // For admins, find first permitted route
    if (hasPermission('dashboard')) return <Navigate to="/dashboard" replace />;
    if (hasPermission('members')) return <Navigate to="/members" replace />;
    if (hasPermission('trainers')) return <Navigate to="/trainers" replace />;
    if (hasPermission('plans')) return <Navigate to="/membership-plans" replace />;
    if (hasPermission('subscriptions')) return <Navigate to="/subscriptions" replace />;
    if (hasPermission('attendance')) return <Navigate to="/attendance" replace />;
    if (hasPermission('payments')) return <Navigate to="/payments" replace />;
    if (hasPermission('settings')) return <Navigate to="/settings" replace />;
    
    return (
      <div className="flex items-center justify-center h-screen bg-zinc-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Access Denied</h1>
          <p className="text-zinc-500">You don't have permission to access any modules.</p>
          <button 
            onClick={() => window.location.href = '/login'}
            className="mt-4 px-4 py-2 bg-lime-400 text-zinc-900 font-bold rounded hover:bg-lime-500"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/superuser/login" element={<PublicRoute><SuperuserLogin /></PublicRoute>} />

            {/* Superuser Routes */}
            <Route path="/superuser" element={<ProtectedRoute requireSuperuser={true}><SuperuserLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<CompanyManagement />} />
            </Route>

            {/* Member Routes */}
            <Route path="/member" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="dashboard" element={<MemberDashboard />} />
              <Route path="subscriptions" element={<MemberSubscriptions />} />
              <Route path="attendance" element={<MemberAttendance />} />
              <Route path="payments" element={<MemberLedger />} />
              <Route path="health" element={<MemberHealth />} />
            </Route>

            {/* Admin/Regular Routes */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<HomeRedirect />} />
              <Route path="dashboard" element={<ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>} />
              <Route path="members" element={<ProtectedRoute permission="members"><Members /></ProtectedRoute>} />
              <Route path="members/:memberId" element={<ProtectedRoute permission="members"><MemberDetail /></ProtectedRoute>} />
              <Route path="trainers" element={<ProtectedRoute permission="trainers"><Trainers /></ProtectedRoute>} />
              <Route path="trainers/:trainerId" element={<ProtectedRoute permission="trainers"><TrainerDetail /></ProtectedRoute>} />
              <Route path="membership-plans" element={<ProtectedRoute permission="plans"><MembershipPlans /></ProtectedRoute>} />
              <Route path="subscriptions" element={<ProtectedRoute permission="subscriptions"><Subscriptions /></ProtectedRoute>} />
              <Route path="attendance" element={<ProtectedRoute permission="attendance"><Attendance /></ProtectedRoute>} />
              <Route path="payments" element={<ProtectedRoute permission="payments"><Payments /></ProtectedRoute>} />
              <Route path="financial-ledger" element={<ProtectedRoute permission="payments"><FinancialLedger /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute permission="settings"><Settings /></ProtectedRoute>} />
              <Route path="company-settings" element={<ProtectedRoute permission="settings"><CompanySettings /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute permission="settings"><UserManagement /></ProtectedRoute>} />
            </Route>

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </SettingsProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
